// components/OdemeModal.js
import React, { useState, useEffect } from 'react';
import axiosConfig from "../../../../api/axiosConfig";
import { parseMoneyInput, sanitizeMoneyInput } from "../../../../utils/moneyUtils";

const OdemeModal = ({ isOpen, onClose, studentInfo, onPaymentAdded }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [banks, setBanks] = useState([]); // Banka listesi için state
  const [showQuickBankAdd, setShowQuickBankAdd] = useState(false); // Hızlı banka ekleme formu
  const [newBankName, setNewBankName] = useState(''); // Yeni banka adı
  const [addingBank, setAddingBank] = useState(false); // Banka ekleme durumu
  const [formData, setFormData] = useState({
    banka_ismi: '',
    tarih: new Date().toISOString().split('T')[0], 
    tutar: '',
    admin_id: null
  });

  useEffect(() => {
    if (isOpen && studentInfo) {
      fetchPayments();
      fetchBanks(); // Bankaları da yükle
    }
  }, [isOpen, studentInfo]);

  // Bankaları yükle
  const fetchBanks = async () => {
    try {
      const response = await axiosConfig.get('/admin/banks/active');
      setBanks(response.data.banks || []);
    } catch (error) {
      console.error('Bankalar yüklenirken hata:', error);
      setBanks([]);
    }
  };

  // Hızlı banka ekleme
  const handleQuickBankAdd = async () => {
    if (!newBankName.trim()) {
      alert('Banka adı giriniz!');
      return;
    }

    setAddingBank(true);
    try {
      const response = await axiosConfig.post('/admin/banks', {
        name: newBankName.trim(),
        isActive: true
      });
      
      // Bankaları yeniden yükle
      await fetchBanks();
      
      // Yeni eklenen bankayı seç
      setFormData({...formData, banka_ismi: newBankName.trim()});
      
      // Formu temizle ve kapat
      setNewBankName('');
      setShowQuickBankAdd(false);
      
      alert('Banka başarıyla eklendi!');
    } catch (error) {
      console.error('Banka eklenirken hata:', error);
      alert(error.response?.data?.message || 'Banka eklenirken hata oluştu!');
    } finally {
      setAddingBank(false);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await axiosConfig.get(`/admin/odemes/musteri/${studentInfo.id}`);
      setPayments(response.data);
    } catch (error) {
      console.error('Ödemeler alınırken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amount = parseMoneyInput(formData.tutar);
    if (formData.tutar === '' || Number.isNaN(amount) || amount === 0) {
      alert('Geçerli bir tutar giriniz! (0 olamaz)');
      return;
    }

    try {
      const submitData = {
        ...formData,
        musteri_id: studentInfo.id,
        tutar: amount
      };

      if (editingPayment) {
        await axiosConfig.put(`/admin/odemes/${editingPayment.id}`, submitData);
      } else {
        await axiosConfig.post('/admin/odemes', submitData);
      }

      await fetchPayments();
      resetForm();
      if (onPaymentAdded) onPaymentAdded();
    } catch (error) {
      console.error('Ödeme kaydedilirken hata:', error);
      alert('Ödeme kaydedilirken bir hata oluştu!');
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      banka_ismi: payment.banka_ismi || '',
      tarih: payment.tarih || '',
      tutar: payment.tutar || '',
      admin_id: payment.admin_id
    });
    setShowForm(true);
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Bu ödemeyi silmek istediğinize emin misiniz?')) return;
    
    try {
      await axiosConfig.delete(`/admin/odemes/${paymentId}`);
      await fetchPayments();
      if (onPaymentAdded) onPaymentAdded();
    } catch (error) {
      console.error('Ödeme silinirken hata:', error);
      alert('Ödeme silinirken bir hata oluştu!');
    }
  };

  const resetForm = () => {
    setFormData({
      banka_ismi: '',
      tarih: new Date().toISOString().split('T')[0],
      tutar: '',
      admin_id: null
    });
    setEditingPayment(null);
    setShowForm(false);
    setShowQuickBankAdd(false); // Hızlı banka ekleme formunu da kapat
    setNewBankName(''); // Yeni banka adını temizle
  };

  const getTotalPayments = () => {
    return payments.reduce((total, payment) => total + parseFloat(payment.tutar || 0), 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-white">
              {studentInfo?.name || 'Öğrenci'} - Ödeme Yönetimi
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Özet Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-600">Toplam Ödeme</p>
                  <p className="text-xl font-bold text-blue-900">{getTotalPayments().toFixed(2)} ₺</p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-600">Ödeme Sayısı</p>
                  <p className="text-xl font-bold text-green-900">{payments.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-purple-600">Son Ödeme</p>
                  <p className="text-xl font-bold text-purple-900">
                    {payments.length > 0 ? 
                      payments.sort((a, b) => new Date(b.tarih) - new Date(a.tarih))[0]?.tarih?.split('T')[0] || '-'
                      : '-'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Yeni Ödeme Butonu */}
          <div className="mb-6">
            <button
              onClick={() => setShowForm(!showForm)}
              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {showForm ? 'Formu Kapat' : 'Yeni Ödeme Ekle'}
            </button>
          </div>

          {/* Ödeme Formu */}
          {showForm && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h4 className="text-lg font-semibold mb-4">
                {editingPayment ? 'Ödeme Düzenle' : 'Yeni Ödeme Ekle'}
              </h4>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Banka Adı
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={formData.banka_ismi}
                      onChange={(e) => setFormData({...formData, banka_ismi: e.target.value})}
                      className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="">Banka Seçiniz</option>
                      {banks.map((bank) => (
                        <option key={bank.id || bank._id} value={bank.name}>
                          {bank.name}
                        </option>
                      ))}
                      <option value="Nakit">Nakit</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowQuickBankAdd(!showQuickBankAdd)}
                      className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      title="Hızlı Banka Ekle"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Hızlı Banka Ekleme Formu */}
                  {showQuickBankAdd && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newBankName}
                          onChange={(e) => setNewBankName(e.target.value)}
                          placeholder="Yeni banka adı..."
                          className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          disabled={addingBank}
                        />
                        <button
                          type="button"
                          onClick={handleQuickBankAdd}
                          disabled={addingBank}
                          className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors disabled:opacity-50"
                        >
                          {addingBank ? 'Ekleniyor...' : 'Ekle'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowQuickBankAdd(false);
                            setNewBankName('');
                          }}
                          className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition-colors"
                        >
                          İptal
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tarih *
                  </label>
                  <input
                    type="date"
                    value={formData.tarih}
                    onChange={(e) => setFormData({...formData, tarih: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tutar (₺) *
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formData.tutar}
                    onChange={(e) => setFormData({...formData, tutar: sanitizeMoneyInput(e.target.value)})}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Örn: 50,75"
                    required
                  />
                </div>
                
                <div className="md:col-span-3 flex gap-2">
                  <button
                    type="submit"
                    className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    {editingPayment ? 'Güncelle' : 'Kaydet'}
                  </button>
                  {editingPayment && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      İptal
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}

          {/* Ödemeler Listesi */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Ödeme Geçmişi</h4>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                <p className="mt-2 text-gray-500">Yükleniyor...</p>
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-gray-500">Henüz ödeme kaydı bulunmuyor</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Banka</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tutar</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {payments
                      .sort((a, b) => new Date(b.tarih) - new Date(a.tarih))
                      .map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {payment.tarih ? new Date(payment.tarih).toLocaleDateString('tr-TR') : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {payment.banka_ismi || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          {parseFloat(payment.tutar || 0).toFixed(2)} ₺
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(payment)}
                              className="text-blue-600 hover:text-blue-900 transition-colors"
                              title="Düzenle"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDelete(payment.id)}
                              className="text-red-600 hover:text-red-900 transition-colors"
                              title="Sil"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OdemeModal;
